import type { DoneDay } from '@/features/project-board/types';

/**
 * 처리량(팀 속도) 집계 — task 완료 + 이슈 완료.
 *
 * 세는 일은 서버가 한다(`GET /v1/board/throughput`) — 여기서는 날짜별 건수를 고른 구간으로 묶기만 한다.
 *
 * 완료 시점의 출처는 task는 PROGRESS.md의 `Updated At`, 이슈는 마지막 처리 커밋 시각이다 — 둘 다
 * **완료일이 아니라 마지막 갱신일**이므로 근사값이다. 날짜가 없는 완료는 서버가 `date = null`로 모아
 * 주고, 여기서 `undated`로 드러낸다.
 *
 * AI-DART가 무인으로 처리한 이슈는 `aiDart`로 따로 쌓는다 — 사람 처리량과 합치면 팀 속도가 부풀어
 * 보인다. `done`은 둘을 합친 총량(막대 전체 높이), `aiDart`는 그중 AI-DART 몫(위 칸)이다.
 */
export type Period = 'day' | 'week' | 'month';

export interface Bucket {
  /** 구간 시작(YYYY-MM-DD). 같은 구간 판정 키로도 쓴다. */
  start: string;
  label: string;
  /** 총 완료(사람 + AI-DART). */
  done: number;
  /** 그중 AI-DART가 무인 처리한 이슈 수 — `done`의 부분집합이다. */
  aiDart: number;
  /** 그중 사람이 처리한 몫 — `done - aiDart`를 화면마다 다시 계산하지 않게 여기서 낸다. */
  general: number;
}

export interface Throughput {
  period: Period;
  buckets: Bucket[];
  current: number;
  previous: number;
  /** 직전 구간 대비 증감률. 직전이 0이면 null(비율을 만들 수 없다). */
  changePct: number | null;
  /** 직전 4구간 평균 — 한 구간의 튐에 흔들리지 않는 기준선. */
  average4: number;
  /** 날짜가 없어 추세에서 빠진 완료 수 — 숨기지 않고 드러낸다. */
  undated: number;
}

const DAY = 86_400_000;

export const PERIOD_LABEL: Record<Period, { unit: string; previous: string; count: number }> = {
  day: { unit: '일', previous: '전일', count: 14 },
  week: { unit: '주', previous: '전주', count: 12 },
  month: { unit: '월', previous: '전월', count: 12 },
};

export class ThroughputSeries {
  static toIso(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  /** 그 날짜가 속한 구간의 시작일. 주는 월요일 시작(스프린트 감각과 맞춘다). */
  static bucketStart(date: Date, period: Period): string {
    if (period === 'day') return ThroughputSeries.toIso(date);
    if (period === 'month') return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
    return ThroughputSeries.toIso(new Date(date.getTime() - ((date.getDay() + 6) % 7) * DAY));
  }

  static shift(start: string, period: Period, back: number): string {
    const base = new Date(`${start}T12:00:00`);
    if (period === 'month') return ThroughputSeries.bucketStart(new Date(base.getFullYear(), base.getMonth() - back, 1), 'month');
    const days = period === 'day' ? back : back * 7;
    return ThroughputSeries.bucketStart(new Date(base.getTime() - days * DAY), period);
  }

  static labelOf(start: string, period: Period): string {
    if (period === 'month') return `${start.slice(2, 4)}/${start.slice(5, 7)}`;
    return `${start.slice(5, 7)}/${start.slice(8, 10)}`;
  }

  static parseDate(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  static of(days: DoneDay[], now: Date, period: Period, bucketCount = PERIOD_LABEL[period].count): Throughput {
    // 저장소가 여럿이면 같은 날짜가 여러 행으로 온다 — 구간 키로 합친다. AI-DART 몫은 총량과 별도로 쌓는다.
    const perBucket = new Map<string, { done: number; aiDart: number }>();
    let undated = 0;
    for (const day of days) {
      const date = day.date === null ? null : ThroughputSeries.parseDate(day.date);
      if (date === null) {
        undated += day.done;
        continue;
      }
      const key = ThroughputSeries.bucketStart(date, period);
      const entry = perBucket.get(key) ?? { done: 0, aiDart: 0 };
      entry.done += day.done;
      if (day.origin === 'ai-dart') entry.aiDart += day.done;
      perBucket.set(key, entry);
    }

    const currentStart = ThroughputSeries.bucketStart(now, period);
    const count = bucketCount;
    const buckets: Bucket[] = [];
    for (let back = count - 1; back >= 0; back -= 1) {
      const start = ThroughputSeries.shift(currentStart, period, back);
      const entry = perBucket.get(start);
      const done = entry?.done ?? 0;
      const aiDart = entry?.aiDart ?? 0;
      buckets.push({ start, label: ThroughputSeries.labelOf(start, period), done, aiDart, general: done - aiDart });
    }

    const current = buckets.at(-1)?.done ?? 0;
    const previous = buckets.at(-2)?.done ?? 0;
    const recent = buckets.slice(-5, -1);

    return {
      period,
      buckets,
      current,
      previous,
      changePct: previous === 0 ? null : Math.round(((current - previous) / previous) * 100),
      average4: recent.length === 0 ? 0 : Math.round(recent.reduce((sum, bucket) => sum + bucket.done, 0) / recent.length),
      undated,
    };
  }
}
